import React, { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function SingleSelectFilter({
  placeholder = "Select…",
  options = [],
  value = "",
  onChange,
  testIdPrefix = "single-select-filter",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const found = options.find((o) => (typeof o === "string" ? o === value : o?.value === value));
    if (!found) return "";
    return typeof found === "string" ? found : found.label;
  }, [options, value]);

  const normalized = useMemo(() => {
    return options
      .map((o) => {
        if (typeof o === "string") return { value: o, label: o };
        return { value: o.value, label: o.label };
      })
      .filter((o) => o.value);
  }, [options]);

  return (
    <Popover open={disabled ? false : open} onOpenChange={(next) => (!disabled ? setOpen(next) : null)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          className="h-auto min-h-10 w-full justify-between rounded-xl bg-white/5 px-3 py-2 text-left text-slate-100 ring-1 ring-white/10 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid={`${testIdPrefix}-trigger`}
        >
          {selectedLabel ? (
            <span className="truncate text-sm text-slate-100">{selectedLabel}</span>
          ) : (
            <span className="truncate text-sm text-slate-200/60">{placeholder}</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-slate-200/70" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="z-[60] w-[340px] border-white/10 bg-slate-950 p-2 text-slate-100 shadow-2xl shadow-black/60" align="start">
        <Command className="bg-transparent text-slate-100">
          <CommandInput
            placeholder="Type to search…"
            className="text-slate-100 placeholder:text-slate-200/50"
            data-testid={`${testIdPrefix}-search`}
          />
          <CommandList className="max-h-72 overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-slate-200/70">No results.</CommandEmpty>
            {normalized.map((opt, idx) => (
              <CommandItem
                key={opt.value}
                value={opt.label}
                onSelect={() => {
                  onChange?.(opt.value);
                  setOpen(false);
                }}
                className="text-slate-100 data-[selected=true]:bg-white/10 data-[selected=true]:text-slate-100"
                data-testid={`${testIdPrefix}-option-${idx}`}
              >
                <span className="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-sm ring-1 ring-white/20">
                  {value === opt.value ? <Check className="h-3 w-3" /> : null}
                </span>
                <span className="truncate">{opt.label}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
